"use client";

import { Html } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { useT } from "@/Hooks";
import { CommentsSectionProps } from "@/Templates/SectionsProps";
import { Rate } from "antd";
import { Autoplay } from "swiper/modules";
import {
  Children,
  cloneElement,
  FC,
  isValidElement,
  ReactElement,
  ReactNode,
} from "react";
import HomeEighteenTestimonialSectionGenerated, {
  HomeEighteenTestimonialSectionGeneratedProps,
} from "../Sections/_generated/HomeEighteenTestimonialSection.generated";
import HomeElevenTestimonailSectionGenerated, {
  HomeElevenTestimonailSectionGeneratedProps,
} from "../Sections/_generated/HomeElevenTestimonailSection.generated";
import HomeFifteenExpertsSectionGenerated, {
  HomeFifteenExpertsSectionGeneratedProps,
} from "../Sections/_generated/HomeFifteenExpertsSection.generated";
import HomeFiveReviewsSectionGenerated, {
  HomeFiveReviewsSectionGeneratedProps,
} from "../Sections/_generated/HomeFiveReviewsSection.generated";
import HomeFourteenTestimonialsSectionGenerated, {
  HomeFourteenTestimonialsSectionGeneratedProps,
} from "../Sections/_generated/HomeFourteenTestimonialsSection.generated";
import HomeFourTestimonialSectionGenerated, {
  HomeFourTestimonialSectionGeneratedProps,
} from "../Sections/_generated/HomeFourTestimonialSection.generated";
import HomeNineTestimonialSectionGenerated, {
  HomeNineTestimonialSectionGeneratedProps,
} from "../Sections/_generated/HomeNineTestimonialSection.generated";
import HomeOneTestimonialSectionGenerated, {
  HomeOneTestimonialSectionGeneratedProps,
} from "../Sections/_generated/HomeOneTestimonialSection.generated";
import HomeSeventeenTestimonialSectionGenerated, {
  HomeSeventeenTestimonialSectionGeneratedProps,
} from "../Sections/_generated/HomeSeventeenTestimonialSection.generated";
import HomeSevenTestimonialSectionGenerated, {
  HomeSevenTestimonialSectionGeneratedProps,
} from "../Sections/_generated/HomeSevenTestimonialSection.generated";
import HomeSixteenTestimonialSectionGenerated, {
  HomeSixteenTestimonialSectionGeneratedProps,
} from "../Sections/_generated/HomeSixteenTestimonialSection.generated";
import HomeSixTestimonialSectionGenerated, {
  HomeSixTestimonialSectionGeneratedProps,
} from "../Sections/_generated/HomeSixTestimonialSection.generated";
import HomeTenTestimonialSectionGenerated, {
  HomeTenTestimonialSectionGeneratedProps,
} from "../Sections/_generated/HomeTenTestimonialSection.generated";
import HomeThirteenTestimonialSectionGenerated, {
  HomeThirteenTestimonialSectionGeneratedProps,
} from "../Sections/_generated/HomeThirteenTestimonialSection.generated";
import HomeThreeTestimonialsSectionGenerated, {
  HomeThreeTestimonialsSectionGeneratedProps,
} from "../Sections/_generated/HomeThreeTestimonialsSection.generated";
import HomeTwelveTestimonialSectionGenerated, {
  HomeTwelveTestimonialSectionGeneratedProps,
} from "../Sections/_generated/HomeTwelveTestimonialSection.generated";
import HomeTwoTestimonialSectionGenerated, {
  HomeTwoTestimonialSectionGeneratedProps,
} from "../Sections/_generated/HomeTwoTestimonialSection.generated";
import {
  buildGeneratedSectionProps,
  renderGeneratedSection,
} from "./generated-section-renderer";

type GeneratedCommentsProps = Record<string, unknown>;
type GeneratedCommentsItem = Record<string, unknown>;
type CommentDataItem = CommentsSectionProps["dataSource"]["Items"][number];
type GeneratedCommentsRenderer = (
  props: CommentsSectionProps,
  anonymousText: string,
) => ReactElement;

const CommentsAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings
    .SiteTemplateCommentsAppearance;

const titleKeyMatchers = [/H20H2Text$/, /H30H3Text$/];
const titleDecorationKeyMatchers = [/H20Span\d+SpanText$/, /H20H2Text2$/];
const subtitleKeyMatchers = [/DivText$/];
const descriptionKeyMatchers = [/P0PText$/, /P0Text$/];

function buildGeneratedCommentsProps<T extends GeneratedCommentsProps>(
  sectionKey: string,
  overrides?: Partial<T>,
): T {
  return buildGeneratedSectionProps<T>(sectionKey, overrides);
}

function findFirstMatchingKey(
  target: GeneratedCommentsProps | GeneratedCommentsItem,
  matchers: RegExp[],
): string | undefined {
  return Object.keys(target).find((key) =>
    matchers.some((matcher) => matcher.test(key)),
  );
}

function setFirstMatchingValue(
  target: GeneratedCommentsProps | GeneratedCommentsItem,
  matchers: RegExp[],
  value: unknown,
): void {
  if (value === null || value === undefined || value === "") {
    return;
  }

  const key = findFirstMatchingKey(target, matchers);
  if (!key) {
    return;
  }

  target[key] = value;
}

function setAllMatchingValues(
  target: GeneratedCommentsProps | GeneratedCommentsItem,
  matchers: RegExp[],
  value: unknown,
): void {
  for (const key of Object.keys(target)) {
    if (matchers.some((matcher) => matcher.test(key))) {
      target[key] = value;
    }
  }
}

function toPersianDate(value: string | undefined): string | undefined {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  return new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

function getCommentAuthor(
  item: CommentDataItem,
  anonymousText: string,
): string {
  return item.CenterUserCreatorFirstName || anonymousText;
}

function getCommentImageUrl(item: CommentDataItem): string | undefined {
  return item.CenterUserCreatorImage?.Url || undefined;
}

function renderHomeOneComments(
  props: CommentsSectionProps,
  anonymousText: string,
): ReactElement {
  return (
    <HomeOneTestimonialSectionGenerated
      {...applyCommentsItems(
        buildGeneratedCommentsProps<HomeOneTestimonialSectionGeneratedProps>(
          "HomeOneTestimonialSection",
        ),
        props,
        anonymousText,
      )}
      testimonialSectionCountersRenderer={() => null}
    />
  );
}

function renderHomeTwoComments(
  props: CommentsSectionProps,
  anonymousText: string,
): ReactElement {
  return (
    <HomeTwoTestimonialSectionGenerated
      {...applyHomeTwoItems(
        buildGeneratedCommentsProps<HomeTwoTestimonialSectionGeneratedProps>(
          "HomeTwoTestimonialSection",
        ),
        props,
        anonymousText,
      )}
    />
  );
}

function renderHomeThreeComments(
  props: CommentsSectionProps,
  anonymousText: string,
): ReactElement {
  return (
    <HomeThreeTestimonialsSectionGenerated
      {...applyHomeThreeItems(
        buildGeneratedCommentsProps<HomeThreeTestimonialsSectionGeneratedProps>(
          "HomeThreeTestimonialsSection",
        ),
        props,
        anonymousText,
      )}
    />
  );
}

function renderHomeFourComments(
  props: CommentsSectionProps,
  anonymousText: string,
): ReactElement {
  const generatedProps = applyHomeFourItems(
    buildGeneratedCommentsProps<HomeFourTestimonialSectionGeneratedProps>(
      "HomeFourTestimonialSection",
    ),
    props,
    anonymousText,
  );

  return (
    <HomeFourTestimonialSectionGenerated
      {...generatedProps}
      sectionDiv0Swiper0SwiperProps={{
        ...(generatedProps.sectionDiv0Swiper0SwiperProps ?? {}),
        modules: [Autoplay],
      }}
    />
  );
}

function isLikelyCommentsItemsArray(
  items: unknown[],
): items is GeneratedCommentsItem[] {
  const firstItem = items[0];
  if (!firstItem || typeof firstItem !== "object") {
    return false;
  }

  const keys = Object.keys(firstItem as GeneratedCommentsItem);
  const contentScore = keys.filter((key) =>
    /(h30Text|a0Text|link0Text|p0Text|p0P0Text|div0Text|span0Text)$/i.test(key),
  ).length;

  return contentScore >= 2;
}

function findCommentsItemsKey(
  props: GeneratedCommentsProps,
): string | undefined {
  return Object.entries(props).find(([key, value]) => {
    if (!Array.isArray(value) || value.length === 0) {
      return false;
    }

    if (!isLikelyCommentsItemsArray(value)) {
      return false;
    }

    return !/(star|fasfastar|fasfastarfilled|counter|avataravatarrounded|servicesslide|horizontalslide)/i.test(
      key,
    );
  })?.[0];
}

function applyCommentContent(
  baseItem: GeneratedCommentsItem,
  comment: CommentDataItem,
  anonymousText: string,
): GeneratedCommentsItem {
  const nextItem: GeneratedCommentsItem = {
    ...baseItem,
    key: comment.Id,
  };
  const author = getCommentAuthor(comment, anonymousText);
  const dateText = toPersianDate(comment.DateRegister);
  const metaText = comment.ProvinceName || dateText || "";
  const imageUrl = getCommentImageUrl(comment);

  if ("href" in nextItem) {
    nextItem.href = "#";
  }
  if ("a0Href" in nextItem) {
    nextItem.a0Href = "#";
  }
  if ("link0Href" in nextItem) {
    nextItem.link0Href = "#";
  }

  if (imageUrl) {
    if ("image" in nextItem) {
      nextItem.image = imageUrl;
    }
    if ("src" in nextItem) {
      nextItem.src = imageUrl;
    }
    if (!("src" in nextItem) && "imageWithBasePath0Src" in nextItem) {
      nextItem.imageWithBasePath0Src = imageUrl;
    }
  }

  if ("alt" in nextItem) {
    nextItem.alt = author;
  }
  if (!("src" in nextItem) && "imageWithBasePath0Alt" in nextItem) {
    nextItem.imageWithBasePath0Alt = author;
  }

  if ("title" in nextItem) {
    nextItem.title = author;
  }
  if ("author" in nextItem) {
    nextItem.author = author;
  }
  if ("h30Text" in nextItem) {
    nextItem.h30Text = author;
  }
  if ("a0Text" in nextItem) {
    nextItem.a0Text = author;
  }
  if ("link0Text" in nextItem) {
    nextItem.link0Text = author;
  }

  if ("description" in nextItem) {
    nextItem.description = comment.Text;
  }
  if ("location" in nextItem) {
    nextItem.location = metaText;
  }
  if ("p0P0Text" in nextItem) {
    nextItem.p0P0Text = comment.Text;
    if ("p0Text" in nextItem) {
      nextItem.p0Text = metaText;
    }
  } else if ("p0Text" in nextItem) {
    nextItem.p0Text = comment.Text;
    if ("span0Text" in nextItem) {
      nextItem.span0Text = metaText;
    }
    if ("div0Text" in nextItem) {
      nextItem.div0Text = metaText;
    }
  }

  if ("div0Text" in nextItem && !("p0Text" in nextItem)) {
    nextItem.div0Text = comment.Text;
  }

  return nextItem;
}

function applyCommentsSectionOverrides<T extends GeneratedCommentsProps>(
  generatedProps: T,
  props: CommentsSectionProps,
): T {
  const nextProps: GeneratedCommentsProps = {
    ...generatedProps,
  };
  const descriptionNode = props.setting.Description ? (
    <Html value={props.setting.Description} />
  ) : undefined;
  const detailNode = props.setting.Detail ? (
    <Html value={props.setting.Detail} />
  ) : undefined;

  setFirstMatchingValue(nextProps, titleKeyMatchers, props.setting.Title);
  if (props.setting.Title) {
    setAllMatchingValues(nextProps, titleDecorationKeyMatchers, "");
  }
  setFirstMatchingValue(nextProps, subtitleKeyMatchers, detailNode);
  setFirstMatchingValue(nextProps, descriptionKeyMatchers, descriptionNode);

  return nextProps as T;
}

function applyCommentsItems<T extends GeneratedCommentsProps>(
  generatedProps: T,
  props: CommentsSectionProps,
  anonymousText: string,
): T {
  const nextProps = applyCommentsSectionOverrides(
    generatedProps,
    props,
  ) as GeneratedCommentsProps;
  const itemsKey = findCommentsItemsKey(nextProps);
  if (!itemsKey) {
    return nextProps as T;
  }

  const defaults = nextProps[itemsKey];
  if (
    !Array.isArray(defaults) ||
    defaults.length === 0 ||
    !isLikelyCommentsItemsArray(defaults)
  ) {
    return nextProps as T;
  }

  nextProps[itemsKey] = props.dataSource.Items.map((comment, index) =>
    applyCommentContent(
      defaults[index % defaults.length] ?? {},
      comment,
      anonymousText,
    ),
  );

  return nextProps as T;
}

function applyHomeTwoItems(
  generatedProps: HomeTwoTestimonialSectionGeneratedProps,
  props: CommentsSectionProps,
  anonymousText: string,
): HomeTwoTestimonialSectionGeneratedProps {
  const nextProps = applyCommentsSectionOverrides(generatedProps, props);
  const defaults = nextProps.testimonialSectionTestimonialTwos;

  return {
    ...nextProps,
    testimonialSectionTestimonialTwos: props.dataSource.Items.map(
      (comment, index) => ({
        ...(defaults[index % defaults.length] ?? {}),
        key: comment.Id,
        src:
          getCommentImageUrl(comment) ??
          "/Templates/Doccure/img/patients/patient22.jpg",
        alt: getCommentAuthor(comment, anonymousText),
        p0Text: comment.Text,
        h30Text: getCommentAuthor(comment, anonymousText),
        p0P0Text:
          comment.ProvinceName || toPersianDate(comment.DateRegister) || "",
      }),
    ),
  };
}

function applyHomeThreeItems(
  generatedProps: HomeThreeTestimonialsSectionGeneratedProps,
  props: CommentsSectionProps,
  anonymousText: string,
): HomeThreeTestimonialsSectionGeneratedProps {
  const nextProps = applyCommentsSectionOverrides(generatedProps, props);
  const slideDefaults = nextProps.testimonialsSectionHorizontalSlideSlideThrees;
  const itemDefaults = slideDefaults.flatMap(
    (item) => item.slideListItems ?? [],
  );
  const reviewDefaults = itemDefaults.find(
    (item) => item.reviewItems,
  )?.reviewItems;

  return {
    ...nextProps,
    testimonialsSectionHorizontalSlideSlideThrees: [
      {
        ...(slideDefaults[0] ?? {
          key: "comments-slide-left",
          dataDirection: "left",
        }),
        key: slideDefaults[0]?.key ?? "comments-slide-left",
        slideListItems: props.dataSource.Items.map((comment, index) => ({
          ...(itemDefaults[index % itemDefaults.length] ?? {}),
          key: comment.Id,
          src:
            getCommentImageUrl(comment) ??
            "/Templates/Doccure/img/patients/patient2.jpg",
          alt: getCommentAuthor(comment, anonymousText),
          h30Text: getCommentAuthor(comment, anonymousText),
          p0Text:
            comment.ProvinceName || toPersianDate(comment.DateRegister) || "",
          p0P0Text: comment.Text,
          ariaHidden: false,
          reviewItems: reviewDefaults?.map((reviewItem, reviewIndex) => ({
            ...reviewItem,
            key: `${comment.Id}-review-${reviewIndex}`,
          })),
        })),
      },
    ],
  };
}

function applyHomeFourItems(
  generatedProps: HomeFourTestimonialSectionGeneratedProps,
  props: CommentsSectionProps,
  anonymousText: string,
): HomeFourTestimonialSectionGeneratedProps {
  const nextProps = applyCommentsSectionOverrides(generatedProps, props);
  const defaults = nextProps.testimonialSectionSliders;
  const sourceItems = props.dataSource.Items;
  const displayItems =
    sourceItems.length > 0
      ? Array.from(
          { length: Math.max(sourceItems.length, 4) },
          (_, index) => sourceItems[index % sourceItems.length],
        )
      : [];

  return {
    ...nextProps,
    testimonialSectionSliders: displayItems.map((comment, index) => ({
      ...(defaults[index % defaults.length] ?? {}),
      key: `${comment.Id}-${index}`,
      src:
        getCommentImageUrl(comment) ??
        "/Templates/Doccure/img/patients/patient20.jpg",
      alt: getCommentAuthor(comment, anonymousText),
      p0Text: comment.Text,
      p0P0Text: getCommentAuthor(comment, anonymousText),
      p1Text: comment.ProvinceName || toPersianDate(comment.DateRegister) || "",
    })),
  };
}

function applyHomeSevenItems(
  generatedProps: HomeSevenTestimonialSectionGeneratedProps,
  props: CommentsSectionProps,
  anonymousText: string,
): HomeSevenTestimonialSectionGeneratedProps {
  const nextProps = applyCommentsSectionOverrides(generatedProps, props);
  const defaults = nextProps.testimonialSectionSliders;

  return {
    ...nextProps,
    sectionDiv0Div1Div1Div0Slider0SliderProps: {
      ...(nextProps.sectionDiv0Div1Div1Div0Slider0SliderProps ?? {}),
      vertical: false,
      verticalSwiping: false,
      slidesToShow: 1,
      infinite: false,
    },
    testimonialSectionSliders: props.dataSource.Items.map((comment, index) => ({
      ...(defaults[index % defaults.length] ?? {}),
      key: comment.Id,
      src:
        getCommentImageUrl(comment) ??
        "/Templates/Doccure/img/patients/patient20.jpg",
      alt: getCommentAuthor(comment, anonymousText),
      p0Text: comment.Text,
      p0P0Text: getCommentAuthor(comment, anonymousText),
      p1Text: comment.ProvinceName || toPersianDate(comment.DateRegister) || "",
      imageWithBasePath0Alt: getCommentAuthor(comment, anonymousText),
    })),
  };
}

function applyHomeTenItems(
  generatedProps: HomeTenTestimonialSectionGeneratedProps,
  props: CommentsSectionProps,
  anonymousText: string,
): HomeTenTestimonialSectionGeneratedProps {
  const nextProps = applyCommentsSectionOverrides(generatedProps, props);
  const defaults = nextProps.testimonialSectionSliders;

  return {
    ...nextProps,
    sectionDiv0Div0Div1Slider0SliderProps: {
      ...(nextProps.sectionDiv0Div0Div1Slider0SliderProps ?? {}),
      vertical: false,
      verticalSwiping: false,
      slidesToShow: 1,
      infinite: false,
    },
    testimonialSectionSliders: props.dataSource.Items.map((comment, index) => ({
      ...(defaults[index % defaults.length] ?? {}),
      key: comment.Id,
      src:
        getCommentImageUrl(comment) ??
        "/Templates/Doccure/img/patients/patient20.jpg",
      alt: getCommentAuthor(comment, anonymousText),
      p0Text: comment.Text,
      p0P0Text: getCommentAuthor(comment, anonymousText),
      imageWithBasePath0Alt: getCommentAuthor(comment, anonymousText),
    })),
  };
}

function applyHomeElevenItems(
  generatedProps: HomeElevenTestimonailSectionGeneratedProps,
  props: CommentsSectionProps,
  anonymousText: string,
): HomeElevenTestimonailSectionGeneratedProps {
  const nextProps = applyCommentsSectionOverrides(generatedProps, props);
  const testimonialDefaults = nextProps.testimonailSectionTestimonialElevens;
  const authorDefaults = nextProps.testimonailSectionAuthors;
  type HomeElevenTestimonialItem =
    HomeElevenTestimonailSectionGeneratedProps["testimonailSectionTestimonialElevens"][number];

  return {
    ...nextProps,
    testimonailSectionTestimonialElevens: props.dataSource.Items.map(
      (comment, index) => {
        const score = Math.max(0, Math.min(5, Math.round(comment.Score ?? 0)));
        const ratingItems = Array.from({ length: 5 }, (_, starIndex) => ({
          key: `comment-${comment.Id}-star-${starIndex + 1}`,
          className:
            starIndex < score
              ? "fa-solid fa-star filled me-1"
              : "fa-solid fa-star me-1",
        }));

        return {
          ...applyCommentContent(
            testimonialDefaults[index % testimonialDefaults.length] ?? {},
            comment,
            anonymousText,
          ),
          score,
          ratingItems,
        } as unknown as HomeElevenTestimonialItem;
      },
    ),
    testimonailSectionAuthors: props.dataSource.Items.map((comment, index) => ({
      ...(authorDefaults[index % authorDefaults.length] ?? {}),
      key: comment.Id,
      src:
        getCommentImageUrl(comment) ??
        authorDefaults[index % authorDefaults.length]?.src ??
        "/Templates/Doccure/img/patients/patient24.jpg",
      alt: getCommentAuthor(comment, anonymousText),
    })),
  };
}

function renderHomeElevenTestimonialContent(
  content: ReactNode,
  score: number,
): ReactNode {
  if (!isValidElement(content)) {
    return content;
  }

  const elementProps = content.props as {
    children?: ReactNode;
    className?: unknown;
  };
  const className =
    typeof elementProps.className === "string"
      ? elementProps.className
      : undefined;

  if (content.type === "div" && className === "rating") {
    return (
      <div className="rating">
        <Rate disabled defaultValue={score} />
      </div>
    );
  }

  if (content.type === "h4" && className === "author") {
    return null;
  }

  return cloneElement(
    content,
    undefined,
    Children.map(elementProps.children, (child) =>
      renderHomeElevenTestimonialContent(child, score),
    ),
  );
}

function createCommentsRenderer<T extends GeneratedCommentsProps>(
  Component: FC<T>,
  sectionKey: string,
): GeneratedCommentsRenderer {
  // eslint-disable-next-line react/display-name
  return (props, anonymousText) => {
    return (
      <Component
        {...applyCommentsItems(
          buildGeneratedCommentsProps<T>(sectionKey),
          props,
          anonymousText,
        )}
      />
    );
  };
}

const generatedCommentsRenderers: Record<string, GeneratedCommentsRenderer> = {
  [CommentsAppearance.Home1.key]: (props, anonymousText) =>
    renderHomeOneComments(props, anonymousText),
  [CommentsAppearance.Home2.key]: (props, anonymousText) =>
    renderHomeTwoComments(props, anonymousText),
  [CommentsAppearance.Home3.key]: (props, anonymousText) =>
    renderHomeThreeComments(props, anonymousText),
  [CommentsAppearance.Home4.key]: (props, anonymousText) =>
    renderHomeFourComments(props, anonymousText),
  [CommentsAppearance.Home5.key]: (props, anonymousText) => (
    <HomeFiveReviewsSectionGenerated
      {...applyCommentsItems(
        buildGeneratedCommentsProps<HomeFiveReviewsSectionGeneratedProps>(
          "HomeFiveReviewsSection",
        ),
        props,
        anonymousText,
      )}
      reviewsSectionSlides2Renderer={() => null}
    />
  ),
  [CommentsAppearance.Home6.key]:
    createCommentsRenderer<HomeSixTestimonialSectionGeneratedProps>(
      HomeSixTestimonialSectionGenerated,
      "HomeSixTestimonialSection",
    ),
  [CommentsAppearance.Home7.key]: (props, anonymousText) => (
    <HomeSevenTestimonialSectionGenerated
      {...applyHomeSevenItems(
        buildGeneratedCommentsProps<HomeSevenTestimonialSectionGeneratedProps>(
          "HomeSevenTestimonialSection",
        ),
        props,
        anonymousText,
      )}
    />
  ),
  [CommentsAppearance.Home9.key]:
    createCommentsRenderer<HomeNineTestimonialSectionGeneratedProps>(
      HomeNineTestimonialSectionGenerated,
      "HomeNineTestimonialSection",
    ),
  [CommentsAppearance.Home10.key]: (props, anonymousText) => (
    <HomeTenTestimonialSectionGenerated
      {...applyHomeTenItems(
        buildGeneratedCommentsProps<HomeTenTestimonialSectionGeneratedProps>(
          "HomeTenTestimonialSection",
        ),
        props,
        anonymousText,
      )}
    />
  ),
  [CommentsAppearance.Home11.key]: (props, anonymousText) => (
    <HomeElevenTestimonailSectionGenerated
      {...applyHomeElevenItems(
        buildGeneratedCommentsProps<HomeElevenTestimonailSectionGeneratedProps>(
          "HomeElevenTestimonailSection",
        ),
        props,
        anonymousText,
      )}
      testimonailSectionTestimonialElevensItemWrapper={(item, content) =>
        renderHomeElevenTestimonialContent(
          content,
          Number((item as unknown as { score?: number }).score ?? 0),
        )
      }
    />
  ),
  [CommentsAppearance.Home12.key]:
    createCommentsRenderer<HomeTwelveTestimonialSectionGeneratedProps>(
      HomeTwelveTestimonialSectionGenerated,
      "HomeTwelveTestimonialSection",
    ),
  [CommentsAppearance.Home13.key]:
    createCommentsRenderer<HomeThirteenTestimonialSectionGeneratedProps>(
      HomeThirteenTestimonialSectionGenerated,
      "HomeThirteenTestimonialSection",
    ),
  [CommentsAppearance.Home14.key]:
    createCommentsRenderer<HomeFourteenTestimonialsSectionGeneratedProps>(
      HomeFourteenTestimonialsSectionGenerated,
      "HomeFourteenTestimonialsSection",
    ),
  [CommentsAppearance.Home15.key]:
    createCommentsRenderer<HomeFifteenExpertsSectionGeneratedProps>(
      HomeFifteenExpertsSectionGenerated,
      "HomeFifteenExpertsSection",
    ),
  [CommentsAppearance.Home16.key]:
    createCommentsRenderer<HomeSixteenTestimonialSectionGeneratedProps>(
      HomeSixteenTestimonialSectionGenerated,
      "HomeSixteenTestimonialSection",
    ),
  [CommentsAppearance.Home17.key]:
    createCommentsRenderer<HomeSeventeenTestimonialSectionGeneratedProps>(
      HomeSeventeenTestimonialSectionGenerated,
      "HomeSeventeenTestimonialSection",
    ),
  [CommentsAppearance.Home18.key]:
    createCommentsRenderer<HomeEighteenTestimonialSectionGeneratedProps>(
      HomeEighteenTestimonialSectionGenerated,
      "HomeEighteenTestimonialSection",
    ),
};

const GeneratedCommentsSection: FC<CommentsSectionProps> = (props) => {
  const t = useT();
  const anonymousText = t(
    "landing.comment.list.anonymousUser",
    undefined,
    "Anonymous User",
  );
  const renderers = Object.fromEntries(
    Object.entries(generatedCommentsRenderers).map(([key, renderer]) => [
      key,
      (sectionProps: CommentsSectionProps) =>
        renderer(sectionProps, anonymousText),
    ]),
  ) as Record<string, (props: CommentsSectionProps) => ReactElement>;

  return renderGeneratedSection(
    props,
    renderers,
    CommentsAppearance.Home1.key,
    (x) => x.setting.Appearance?.Key,
  );
};

export default GeneratedCommentsSection;
